import styles from './coming-soon-signals.module.scss';
import React, { FC } from 'react';
import { RefreshCw, Target } from 'lucide-react';
import { usePageTracking } from '../../../hooks/usePageTracking';

export type ComingSoonSignal = 'cac' | 'ltv' | 'conversion';

interface PreviewRow {
	dot: string;
	w1: string;
	w2: string;
}

interface ComingSoonConfig {
	color: string;
	colorLight: string;
	title: string;
	previewLabel: string;
	rows: PreviewRow[];
	icon: React.ReactNode;
}

const ConversionIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="#D4760A"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="7" y1="12" x2="17" y2="12" />
		<line x1="10" y1="17" x2="14" y2="17" />
	</svg>
);

const COMING_SOON: Record<ComingSoonSignal, ComingSoonConfig> = {
	cac: {
		color: '#7C3AED',
		colorLight: '#EDE9FE',
		title: 'Customer Acquisition Cost Signals',
		previewLabel: 'Signal preview · CAC efficiency by channel',
		rows: [
			{ dot: '#7C3AED', w1: '65%', w2: '30px' },
			{ dot: '#7C3AED', w1: '50%', w2: '36px' },
			{ dot: '#D4760A', w1: '80%', w2: '28px' },
			{ dot: '#9CA3AF', w1: '40%', w2: '32px' },
		],
		icon: <Target size={28} strokeWidth={1.6} color="#7C3AED" />,
	},
	ltv: {
		color: '#0891B2',
		colorLight: '#E0F7FA',
		title: 'Lifetime Value Signals',
		previewLabel: 'Signal preview · LTV cohort trajectory',
		rows: [
			{ dot: '#2E7D4F', w1: '75%', w2: '34px' },
			{ dot: '#2E7D4F', w1: '60%', w2: '28px' },
			{ dot: '#0891B2', w1: '45%', w2: '38px' },
			{ dot: '#D4760A', w1: '55%', w2: '26px' },
		],
		icon: <RefreshCw size={28} strokeWidth={1.6} color="#0891B2" />,
	},
	conversion: {
		color: '#D4760A',
		colorLight: '#FDF3E7',
		title: 'Conversion Signals',
		previewLabel: 'Signal preview · Conversion triggers this week',
		rows: [
			{ dot: '#2E7D4F', w1: '70%', w2: '30px' },
			{ dot: '#2E7D4F', w1: '55%', w2: '34px' },
			{ dot: '#C0392B', w1: '40%', w2: '28px' },
			{ dot: '#D4760A', w1: '60%', w2: '32px' },
		],
		icon: <ConversionIcon />,
	},
};

const PAGE_TITLES: Record<ComingSoonSignal, string> = {
	cac: 'CAC Signals',
	ltv: 'LTV Signals',
	conversion: 'Conversion Signals',
};

interface ComingSoonSignalsProps {
	signal: ComingSoonSignal;
}

const ComingSoonSignals: FC<ComingSoonSignalsProps> = ({ signal }) => {
	usePageTracking('Page Viewed', PAGE_TITLES[signal]);
	const config = COMING_SOON[signal];

	return (
		<div className={styles.page}>
			<div className={styles.coming_soon}>
				<div className={styles.cs_icon} style={{ color: config.color }}>
					{config.icon}
				</div>
				<div
					className={styles.cs_badge}
					style={{ background: config.colorLight, color: config.color }}
				>
					<span className={styles.cs_pulse} style={{ background: config.color }} />
					Coming Soon
				</div>
				<div className={styles.cs_title}>{config.title}</div>
				<div className={styles.cs_preview}>
					<div className={styles.cs_preview_label}>{config.previewLabel}</div>
					{config.rows.map((row, index) => (
						<div className={styles.cs_row} key={`${signal}-row-${index}`}>
							<div className={styles.cs_dot} style={{ background: row.dot }} />
							<div className={styles.cs_row_text} style={{ width: row.w1 }} />
							<div className={styles.cs_row_num} style={{ width: row.w2 }} />
						</div>
					))}
					<div className={styles.cs_row}>
						<div className={styles.cs_dot} style={{ background: '#E5E5E3' }} />
						<div className={styles.cs_row_text} style={{ width: '35%' }} />
						<div className={styles.cs_row_num} style={{ width: '24px' }} />
					</div>
				</div>
				<div className={styles.cs_footer}>
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
						<circle cx="7" cy="7" r="5.5" />
						<path d="M7 4.5v3l2 1.5" />
					</svg>
					Expected in the next platform update
				</div>
			</div>
		</div>
	);
};

export default ComingSoonSignals;
